// Credential generator - uses API Gateway endpoint for temporary STS upload credentials

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Format credentials as environment variable export statements.
 * Exported for testability (property-based testing).
 */
export function formatEnvVars(credentials) {
    return [
        `export AWS_ACCESS_KEY_ID=${credentials.access_key_id}`,
        `export AWS_SECRET_ACCESS_KEY=${credentials.secret_access_key}`,
        `export AWS_SESSION_TOKEN=${credentials.session_token}`
    ].join('\n');
}

/**
 * Format an AWS CLI usage example with actual bucket and prefix.
 * Exported for testability (property-based testing).
 */
export function formatAwsCliExample(bucket, prefix) {
    return `aws s3 cp ./my-file.img s3://${bucket}/${prefix}my-file.img`;
}

/**
 * Format a Python boto3 usage example with actual bucket, prefix, and region.
 * Exported for testability (property-based testing).
 */
export function formatBoto3Example(bucket, prefix, region) {
    return `import boto3

s3 = boto3.client('s3', region_name='${region}')
s3.upload_file(
    './my-file.img',
    '${bucket}',
    '${prefix}my-file.img'
)`;
}

/**
 * Calculate remaining seconds from an expiration ISO string to now.
 */
function calcRemainingSeconds(expiration) {
    const diff = Math.floor((new Date(expiration).getTime() - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
}

/**
 * Format seconds as MM:SS string.
 */
function formatCountdown(totalSeconds) {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) {
        return `${hrs}h ${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
    }
    return `${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
}

function CredentialGenerator() {
    const [credentials, setCredentials] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [remainingTime, setRemainingTime] = useState(0);
    const [copyFeedback, setCopyFeedback] = useState({});
    const intervalRef = useRef(null);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const startCountdown = useCallback((expiration) => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        const updateRemaining = () => {
            const remaining = calcRemainingSeconds(expiration);
            setRemainingTime(remaining);
            if (remaining <= 0) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        updateRemaining();
        intervalRef.current = setInterval(updateRemaining, 1000);
    }, []);

    const handleGenerate = async () => {
        setLoading(true);
        setError('');
        setCredentials(null);
        setRemainingTime(0);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                throw new Error('No authentication token available. Please sign in again.');
            }

            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/generate-upload-credentials`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ duration_seconds: 3600 })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Session expired. Please sign in again.');
                }
                let errorMsg;
                try {
                    const errData = await response.json();
                    errorMsg = errData.error || errData.message;
                } catch {
                    errorMsg = null;
                }
                if (response.status === 400) {
                    throw new Error(errorMsg || 'Invalid request.');
                }
                throw new Error(errorMsg || 'Failed to generate credentials. Please try again later.');
            }

            const data = await response.json();
            setCredentials(data);
            startCountdown(data.credentials.expiration);
        } catch (err) {
            if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
                setError('Failed to connect. Please check your network and try again.');
            } else {
                setError(err.message || 'An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopyFeedback(prev => ({ ...prev, [fieldName]: true }));
            setTimeout(() => {
                setCopyFeedback(prev => ({ ...prev, [fieldName]: false }));
            }, 2000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopyFeedback(prev => ({ ...prev, [fieldName]: true }));
            setTimeout(() => {
                setCopyFeedback(prev => ({ ...prev, [fieldName]: false }));
            }, 2000);
        }
    };

    const handleCopyAll = () => {
        const envVars = formatEnvVars(credentials.credentials);
        copyToClipboard(envVars, 'all');
    };

    const expired = credentials && remainingTime <= 0;

    return (
        <div className="credential-generator">
            <h3>🔑 Temporary Upload Credentials</h3>
            <p className="credential-description">
                Generate temporary AWS credentials to upload files using the AWS CLI or Python scripts.
            </p>

            <button
                onClick={handleGenerate}
                disabled={loading}
                className="generate-credentials-btn"
            >
                {loading ? 'Generating...' : 'Generate Upload Credentials'}
            </button>

            {loading && (
                <div className="credential-loading">
                    <div className="credential-spinner" />
                    <span>Generating credentials...</span>
                </div>
            )}

            {error && (
                <div className="error-message credential-error">{error}</div>
            )}

            {expired && (
                <div className="credential-expired-message">
                    ⏰ Credentials expired. Click the button above to generate new ones.
                </div>
            )}

            {credentials && !expired && (
                <div className="credential-results">
                    <div className="credential-timer">
                        ⏱ Expires in: <strong>{formatCountdown(remainingTime)}</strong>
                    </div>

                    <div className="credential-fields">
                        {[
                            { label: 'Access Key ID', value: credentials.credentials.access_key_id, key: 'access_key_id' },
                            { label: 'Secret Access Key', value: credentials.credentials.secret_access_key, key: 'secret_access_key' },
                            { label: 'Session Token', value: credentials.credentials.session_token, key: 'session_token' },
                            { label: 'Expiration', value: credentials.credentials.expiration, key: 'expiration' },
                            { label: 'S3 Bucket', value: credentials.s3_bucket, key: 's3_bucket' },
                            { label: 'S3 Prefix', value: credentials.s3_prefix, key: 's3_prefix' },
                        ].map(({ label, value, key }) => (
                            <div key={key} className="credential-field">
                                <label className="credential-label">{label}</label>
                                <div className="credential-value-row">
                                    <code className="credential-value">{value}</code>
                                    <button
                                        onClick={() => copyToClipboard(value, key)}
                                        className="copy-btn"
                                        title={`Copy ${label}`}
                                    >
                                        {copyFeedback[key] ? '✓ Copied' : '📋 Copy'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button onClick={handleCopyAll} className="copy-all-btn">
                        {copyFeedback['all'] ? '✓ Copied!' : '📋 Copy All as Env Vars'}
                    </button>

                    <div className="credential-examples">
                        <h4>Usage Examples</h4>

                        <div className="example-section">
                            <h5>AWS CLI</h5>
                            <pre className="example-code">{`# Set credentials\n${formatEnvVars(credentials.credentials)}\n\n# Upload a file\n${formatAwsCliExample(credentials.s3_bucket, credentials.s3_prefix)}`}</pre>
                        </div>

                        <div className="example-section">
                            <h5>Python (boto3)</h5>
                            <pre className="example-code">{formatBoto3Example(credentials.s3_bucket, credentials.s3_prefix, credentials.region)}</pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CredentialGenerator;
